import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { PortalModal } from "@/components/ui/portal-modal";
import { CircleHelp } from "lucide-react";

interface ConfirmProps {
  open: boolean;
  title: string;
  description: string;
  confirmText?: string;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

export const Confirm = ({ open, title, description, confirmText = "Yes", onConfirm, onClose }: ConfirmProps) => {
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
    } finally {
      setIsConfirming(false);
    }
  };

  useEffect(() => {
    if (!open && isConfirming) {
      setIsConfirming(false);
    }
  }, [open, isConfirming]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onClose();
    }
  };

  return (
    <PortalModal open={open} onOpenChange={handleOpenChange} showCloseButton className="sm:max-w-[420px]">
      <PortalModal.Header className="gap-3 text-center">
        <PortalModal.Title>{title}</PortalModal.Title>
      </PortalModal.Header>
      <PortalModal.Content className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cede-primary/15 text-cede-primary">
            <CircleHelp className="h-12 w-12" />
          </div>
        </div>
        <p className="text-base text-cede-text-secondary">{description}</p>
      </PortalModal.Content>
      <PortalModal.Footer className="flex w-full flex-col gap-3 pb-2">
        <Button
          variant="primary"
          size="large"
          className="h-12 w-full rounded-2xl text-base font-semibold"
          onClick={handleConfirm}
          loading={isConfirming}
          disabled={isConfirming}
        >
          {confirmText}
        </Button>
        <Button
          type="button"
          variant="transparent-bordered"
          size="large"
          className="h-12 w-full rounded-2xl text-base font-semibold"
          onClick={onClose}
          disabled={isConfirming}
        >
          Cancel
        </Button>
      </PortalModal.Footer>
    </PortalModal>
  );
};
